// src/pages/GrowthChartsPage.jsx
//
// Página dedicada de Tablas de Crecimiento OMS (pediatría).
// Muestra 4 gráficos interactivos (peso, talla, IMC, perímetro cefálico) con
// las curvas de percentiles OMS (P3/P15/P50/P85/P97) y los puntos del paciente
// tomados del histórico de consultas. Lista para imprimir/mostrar a los padres.

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, Grid, Paper, Chip, Stack,
  ToggleButton, ToggleButtonGroup, Alert, Divider, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip as ChartTooltip, Legend, Filler,
} from 'chart.js';
import { useWHOGrowthCharts } from '../hooks/useWHOGrowthCharts';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  Title, ChartTooltip, Legend, Filler
);

// Devuelve una interpretación clínica simple del percentil
function interpretPercentile(p) {
  if (p == null) return { text: '—', color: 'default' };
  if (p < 3) return { text: 'Bajo (< P3)', color: 'error' };
  if (p < 15) return { text: 'Límite bajo (P3–P15)', color: 'warning' };
  if (p <= 85) return { text: 'Normal (P15–P85)', color: 'success' };
  if (p <= 97) return { text: 'Límite alto (P85–P97)', color: 'warning' };
  return { text: 'Alto (> P97)', color: 'error' };
}

const GrowthChartsPage = () => {
  const { id: pacienteId } = useParams();
  const navigate = useNavigate();
  const {
    paciente, loading, error, edadAnios, isPediatric,
    indicators, buildChartData, latestPercentiles, patientPoints,
  } = useWHOGrowthCharts(pacienteId);

  const [activeIndicator, setActiveIndicator] = useState('weight');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Cargando tablas de crecimiento…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>Volver</Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const indicatorMeta = indicators.find((i) => i.key === activeIndicator);
  const chart = buildChartData(activeIndicator);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'nearest', intersect: false },
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 18, usePointStyle: true } },
      title: {
        display: true,
        text: `${indicatorMeta?.label} — ${paciente?.nombres || ''} ${paciente?.apellidos || ''}`,
        font: { size: 15 },
      },
      tooltip: {
        callbacks: {
          title: (items) => `Edad: ${(items[0].parsed.x).toFixed(1)} meses`,
          label: (ctx) => {
            const ds = ctx.dataset.label;
            const val = ctx.parsed.y;
            if (ds === 'Paciente') {
              const pt = (patientPoints[activeIndicator] || []).find(
                (p) => Math.abs(p.x - ctx.parsed.x) < 0.05 && Math.abs(p.y - val) < 0.01
              );
              const pct = pt?.percentile != null ? ` · P${pt.percentile}` : '';
              return `Paciente: ${val} ${indicatorMeta?.unit}${pct}`;
            }
            return `${ds}: ${val} ${indicatorMeta?.unit}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        title: { display: true, text: 'Edad (meses)' },
        min: 0,
        max: chart?.upperMonth || 60,
      },
      y: {
        title: { display: true, text: `${indicatorMeta?.label?.split(' ')[0]} (${indicatorMeta?.unit})` },
      },
    },
  };

  const latest = latestPercentiles[activeIndicator];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      {/* Encabezado */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }} className="no-print">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>Volver</Button>
        <Button startIcon={<PrintIcon />} variant="outlined" onClick={() => window.print()}>
          Imprimir / PDF
        </Button>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <ChildCareIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Tablas de Crecimiento OMS</Typography>
            <Typography variant="body2" color="text.secondary">
              {paciente?.nombres} {paciente?.apellidos} · {paciente?.sexo}
              {edadAnios != null && ` · ${edadAnios.toFixed(1)} años`}
            </Typography>
          </Box>
        </Box>

        {!isPediatric && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Este paciente supera los 19 años; las curvas OMS pediátricas pueden no aplicar.
          </Alert>
        )}
      </Paper>

      {/* Selector de indicador */}
      <ToggleButtonGroup
        value={activeIndicator}
        exclusive
        onChange={(e, v) => v && setActiveIndicator(v)}
        sx={{ mb: 3, flexWrap: 'wrap' }}
        className="no-print"
      >
        {indicators.map((ind) => (
          <ToggleButton key={ind.key} value={ind.key} sx={{ textTransform: 'none' }}>
            {ind.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {/* Tarjeta de última medición */}
      {latest && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">Última medición</Typography>
              <Typography variant="h6">{latest.y} {indicatorMeta?.unit}</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary">Percentil</Typography>
              <Typography variant="h6">P{latest.percentile} (z {latest.z})</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary">Interpretación</Typography><br />
              <Chip
                size="small"
                label={interpretPercentile(latest.percentile).text}
                color={interpretPercentile(latest.percentile).color}
              />
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Gráfico */}
      <Paper elevation={0} sx={{ p: { xs: 1, md: 3 }, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
        {chart && (patientPoints[activeIndicator]?.length ?? 0) === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} className="no-print">
            No hay mediciones de <b>{indicatorMeta?.label?.toLowerCase()}</b> registradas en las consultas
            de este paciente. Las curvas de referencia OMS se muestran igual.
          </Alert>
        )}
        <Box sx={{ height: { xs: 360, md: 480 } }}>
          {chart && <Line data={{ datasets: chart.datasets }} options={chartOptions} />}
        </Box>
      </Paper>

      {/* Tabla de mediciones */}
      {(patientPoints[activeIndicator]?.length ?? 0) > 0 && (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Historial de mediciones — {indicatorMeta?.label}
          </Typography>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { textAlign: 'left', py: 0.75, borderBottom: '1px solid', borderColor: 'divider', fontSize: 14 } }}>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Edad (meses)</th>
                <th>Valor ({indicatorMeta?.unit})</th>
                <th>Percentil</th>
                <th>z-score</th>
                <th>Interpretación</th>
              </tr>
            </thead>
            <tbody>
              {patientPoints[activeIndicator].map((p, i) => {
                const interp = interpretPercentile(p.percentile);
                return (
                  <tr key={i}>
                    <td>{new Date(p.fecha).toLocaleDateString()}</td>
                    <td>{p.x.toFixed(1)}</td>
                    <td>{p.y}</td>
                    <td>P{p.percentile}</td>
                    <td>{p.z}</td>
                    <td><Chip size="small" label={interp.text} color={interp.color} variant="outlined" /></td>
                  </tr>
                );
              })}
            </tbody>
          </Box>
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
        Curvas de referencia: WHO Child Growth Standards (0–5 años) y WHO Reference 2007 (5–19 años).
      </Typography>

      <style>{`@media print { .no-print { display: none !important; } }`}</style>
    </Box>
  );
};

export default GrowthChartsPage;
